import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Typography, Chip, Grid } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { renderField } from './fieldRenderers';
import { extractDataElementValues } from './components/DataElementChips';
import { renderItemsObject } from './specialRenderers';

export const renderChartMetadataTable = (chartData, chart, classes) => {
	const rows = [];

	// _id first
	if (chart?._id) {
		rows.push({ property: '_id', value: chart._id });
	}
	if (chartData?.name) {
		rows.push({ property: 'name', value: chartData.name });
	}
	if (chart?.chartUniqueId) {
		rows.push({ property: 'chartUniqueId', value: chart.chartUniqueId });
	}
	if (chart?.chartType) {
		rows.push({ property: 'chartType', value: chart.chartType });
	}
	if (chartData?.selectedDataModel) {
		rows.push({ property: 'selectedDataModel', value: chartData.selectedDataModel });
	}
	if (chartData?.selectedTopLevelDataElement && Array.isArray(chartData.selectedTopLevelDataElement)) {
		const values = chartData.selectedTopLevelDataElement
			.map((item) => {
				if (typeof item === 'object' && item !== null) {
					return item.value || item.label || item.name || JSON.stringify(item);
				}
				return String(item);
			})
			.filter((val) => val !== undefined && val !== null);
		rows.push({
			property: 'selectedTopLevelDataElement',
			value: values,
			isChips: true
		});
	}
	if (chartData?.dataUrl) {
		rows.push({ property: 'dataUrl', value: chartData.dataUrl });
	}
	if (chartData?.fetchType) {
		rows.push({ property: 'fetchType', value: chartData.fetchType });
	}
	if (chartData?.annotationRedirectUrl) {
		rows.push({ property: 'annotationRedirectUrl', value: chartData.annotationRedirectUrl });
	}
	if (chartData?.externalFilter !== undefined && chartData?.externalFilter !== null) {
		if (Array.isArray(chartData.externalFilter)) {
			const values = chartData.externalFilter
				.map((item) => {
					if (typeof item === 'object' && item !== null) {
						return item.value || item.label || item.name || JSON.stringify(item);
					}
					return String(item);
				})
				.filter((val) => val !== undefined && val !== null);
			rows.push({
				property: 'externalFilter',
				value: values,
				isChips: true
			});
		} else if (typeof chartData.externalFilter === 'object') {
			// For object, we'll mark it to render as a sub-table
			rows.push({
				property: 'externalFilter',
				value: chartData.externalFilter,
				isObjectTable: true
			});
		} else {
			rows.push({ property: 'externalFilter', value: chartData.externalFilter });
		}
	}

	if (rows.length === 0) {
		return null;
	}

	return (
		<TableContainer component={Paper} style={{ marginTop: 8, marginBottom: 16 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
						<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row, index) => (
						<TableRow key={index}>
							<TableCell>{row.property}:</TableCell>
							<TableCell>
								{row.isChips && Array.isArray(row.value) ? (
									<Box display="flex" flexWrap="wrap">
										{row.value.map((val, idx) => (
											<Chip
												key={idx}
												label={val}
												size="small"
												style={{ marginRight: 8, marginBottom: 8 }}
											/>
										))}
									</Box>
								) : row.isObjectTable && typeof row.value === 'object' ? (
									renderItemsObject(row.value, classes)
								) : (
									String(row.value)
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export const renderConditionsTable = (conditionsArray) => {
	if (!Array.isArray(conditionsArray) || conditionsArray.length === 0) {
		return <Typography color="textSecondary">(No conditions)</Typography>;
	}

	return (
		<TableContainer component={Paper} style={{ marginTop: 16 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>selectedConditionClause</TableCell>
						<TableCell>selectedConditionDataElement</TableCell>
						<TableCell>selectedConditionOperator</TableCell>
						<TableCell>value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{conditionsArray.map((condition, index) => (
						<TableRow key={index}>
							<TableCell>{condition.selectedConditionClause || '-'}</TableCell>
							<TableCell>{condition.selectedConditionDataElement || '-'}</TableCell>
							<TableCell>{condition.selectedConditionOperator || '-'}</TableCell>
							<TableCell>{condition.value || '-'}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export const renderLayoutTable = (layoutObj) => {
	if (!layoutObj || typeof layoutObj !== 'object') {
		return <Typography color="textSecondary">(No layout data)</Typography>;
	}

	return (
		<TableContainer component={Paper} style={{ marginTop: 8 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>w</TableCell>
						<TableCell>h</TableCell>
						<TableCell>x</TableCell>
						<TableCell>y</TableCell>
						<TableCell>moved</TableCell>
						<TableCell>static</TableCell>
						<TableCell>i</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell>{layoutObj.w !== undefined ? layoutObj.w : '-'}</TableCell>
						<TableCell>{layoutObj.h !== undefined ? layoutObj.h : '-'}</TableCell>
						<TableCell>{layoutObj.x !== undefined ? layoutObj.x : '-'}</TableCell>
						<TableCell>{layoutObj.y !== undefined ? layoutObj.y : '-'}</TableCell>
						<TableCell>{String(layoutObj.moved !== undefined ? layoutObj.moved : '-')}</TableCell>
						<TableCell>{String(layoutObj.static !== undefined ? layoutObj.static : '-')}</TableCell>
						<TableCell>{layoutObj.i !== undefined ? layoutObj.i : '-'}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export const renderExternalFilters = (externalFiltersArray) => {
	if (!Array.isArray(externalFiltersArray) || externalFiltersArray.length === 0) {
		return <Typography color="textSecondary">(No external filters)</Typography>;
	}

	// Extract values from filters and display as chips
	const values = [];
	externalFiltersArray.forEach((filter) => {
		if (typeof filter === 'object' && filter !== null) {
			// Get all values from the filter object
			Object.values(filter).forEach((val) => {
				if (val !== undefined && val !== null && val !== '') {
					values.push(String(val));
				}
			});
		} else {
			values.push(String(filter));
		}
	});

	if (values.length === 0) {
		return <Typography color="textSecondary">(Empty)</Typography>;
	}

	return (
		<Box display="flex" flexWrap="wrap">
			{values.map((val, idx) => (
				<Chip
					key={idx}
					label={val}
					size="small"
					style={{ marginRight: 8, marginBottom: 8 }}
				/>
			))}
		</Box>
	);
};

export const renderSplitPane = (splitPaneData) => {
	if (!splitPaneData) {
		return <Typography color="textSecondary">(No split pane data)</Typography>;
	}

	// Handle array of split panes
	if (Array.isArray(splitPaneData)) {
		if (splitPaneData.length === 0) {
			return <Typography color="textSecondary">(No split pane data)</Typography>;
		}

		// Collect all unique keys from all pane objects
		const allKeys = new Set();
		splitPaneData.forEach((item) => {
			if (typeof item === 'object' && item !== null) {
				Object.keys(item).forEach((key) => allKeys.add(key));
			} else {
				allKeys.add('value');
			}
		});

		const columns = Array.from(allKeys);
		if (columns.length === 0) {
			columns.push('value');
		}

		return (
			<TableContainer component={Paper} style={{ marginTop: 16 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column}>{column}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{splitPaneData.map((pane, index) => (
							<TableRow key={index}>
								{columns.map((column) => {
									let cellValue = '-';
									if (typeof pane === 'object' && pane !== null) {
										if (typeof pane[column] === 'object' && pane[column] !== null && !Array.isArray(pane[column])) {
											cellValue = JSON.stringify(pane[column]);
										} else {
											cellValue = pane[column] !== undefined && pane[column] !== null
												? String(pane[column])
												: '-';
										}
									} else if (column === 'value') {
										cellValue = String(pane);
									}
									return (
										<TableCell key={column}>{cellValue}</TableCell>
									);
								})}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	}

	// Handle single split pane object
	if (typeof splitPaneData === 'object' && splitPaneData !== null) {
		const entries = Object.entries(splitPaneData);
		if (entries.length === 0) {
			return <Typography color="textSecondary">(Empty split pane object)</Typography>;
		}

		return (
			<TableContainer component={Paper} style={{ marginTop: 16 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Property</TableCell>
							<TableCell>Value</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{entries.map(([key, value], index) => (
							<TableRow key={index}>
								<TableCell>{key}</TableCell>
								<TableCell>
									{typeof value === 'object' && value !== null
										? JSON.stringify(value, null, 2)
										: String(value !== undefined && value !== null ? value : '-')}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	}

	return <Typography>{String(splitPaneData)}</Typography>;
};

export const renderBooleanFieldsTable = (data) => {
	if (!data || typeof data !== 'object') {
		return null;
	}

	// List of boolean fields to display in the table
	const booleanFields = [
		'considerErrorsAsWarnings',
		'showHeaderTable',
		'showPdfHighlights',
		'updateInputOnHighlight',
		'searchablePDF',
		'showSearchablePdfDefault',
		'lockSupport',
		'expandLineItems',
		'hidePDF',
		'showTimer',
		'showLineItemInFullScreen',
		'splitPDF',
		'splitpdf'
	];

	// Collect boolean fields that exist in the data
	const booleanEntries = booleanFields
		.filter((field) => data[field] !== undefined && data[field] !== null && (typeof data[field] === 'boolean' || field === 'splitPDF' || field === 'splitpdf'))
		.map((field) => [field, data[field]]);

	if (booleanEntries.length === 0) {
		return null;
	}

	// Render as a single table with all entries
	return (
		<TableContainer component={Paper} style={{ marginTop: 16 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell style={{ fontWeight: 600 }}>Property</TableCell>
						<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{booleanEntries.map(([field, value], index) => {
						const isBoolean = typeof value === 'boolean';
						const isTrue = value === true || value === 'true' || value === 'True';

						return (
							<TableRow key={index}>
								<TableCell>{field}:</TableCell>
								<TableCell>
									{isBoolean || field === 'splitPDF' || field === 'splitpdf' ? (
										isTrue ? (
											<Box display="flex" alignItems="center">
												<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
												<Typography component="span">{String(value)}</Typography>
											</Box>
										) : (
											<Box display="flex" alignItems="center">
												<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
												<Typography component="span">{String(value)}</Typography>
											</Box>
										)
									) : (
										<Typography component="span">{String(value)}</Typography>
									)}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// Render blocks array - each block as a separate section (block1, block2, etc.) with properties in a table
export const renderBlocksTable = (blocksArray, classes) => {
	if (!Array.isArray(blocksArray) || blocksArray.length === 0) {
		return <Typography color="textSecondary">(No blocks data)</Typography>;
	}

	return (
		<Box style={{ marginTop: 8 }}>
			{blocksArray.map((block, index) => {
				if (!block || typeof block !== 'object') {
					return null;
				}

				// Prepare table rows - include selectedDataElement in the main table
				const tableRows = [];

				Object.entries(block).forEach(([key, value]) => {
					// Skip null/undefined values
					if (value === null || value === undefined) {
						return;
					}

					// Special handling for selectedDataElement - display as chips
					if (key === 'selectedDataElement' && Array.isArray(value)) {
						// Extract .value from each object in the array
						const values = value
							.map((item) => {
								if (typeof item === 'object' && item !== null) {
									return item.value || item.label || item.name || JSON.stringify(item);
								}
								return String(item);
							})
							.filter((val) => val !== undefined && val !== null && val !== '');
						tableRows.push({
							key,
							value: values,
							isSelectedDataElement: true
						});
					} else if (key === 'mappingDataElements' && Array.isArray(value)) {
						// Special handling for mappingDataElements - display as chips
						// Extract .value from each object in the array
						const values = value
							.map((item) => {
								if (typeof item === 'object' && item !== null) {
									return item.value || item.label || item.name || JSON.stringify(item);
								}
								return String(item);
							})
							.filter((val) => val !== undefined && val !== null && val !== '');
						tableRows.push({
							key,
							value: values,
							isArrayAsChips: true
						});
					} else if (key === 'targetConditions' && Array.isArray(value)) {
						// Special handling for targetConditions - display as sub-table
						tableRows.push({
							key,
							value: value,
							isSubTable: true
						});
					} else if (Array.isArray(value)) {
						// For other arrays, extract values and display as chips
						const values = extractDataElementValues(value);
						if (values.length > 0) {
							tableRows.push({
								key,
								value: values,
								isArrayAsChips: true
							});
						} else {
							tableRows.push({ key, value: 'Array (0)' });
						}
					} else {
						// Format value for table
						let displayValue;
						if (typeof value === 'boolean') {
							displayValue = String(value);
						} else if (typeof value === 'object') {
							displayValue = `Object (${Object.keys(value).length} keys)`;
						} else {
							displayValue = String(value);
						}
						tableRows.push({ key, value: displayValue });
					}
				});

				return (
					<Box key={index} mb={3} style={{ borderLeft: '3px solid #1976d2', paddingLeft: 12 }}>
						<Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600, marginBottom: 12, color: '#1976d2' }}>
							block{index + 1}:
						</Typography>
						{/* Table for all fields including selectedDataElement */}
						{tableRows.length > 0 && (
							<TableContainer component={Paper} style={{ marginTop: 8 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell style={{ fontWeight: 600, width: '40%' }}>Property</TableCell>
											<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{tableRows.map((row, rowIndex) => (
											<TableRow key={rowIndex}>
												<TableCell>{row.key}:</TableCell>
												<TableCell>
													{row.isSubTable ? (
														// Display as sub-table
														renderConditionsTable(row.value)
													) : row.isSelectedDataElement || row.isArrayAsChips ? (
														// Display arrays as chips
														Array.isArray(row.value) && row.value.length === 0 ? (
															<Typography color="textSecondary">(Empty)</Typography>
														) : Array.isArray(row.value) ? (
															<Box display="flex" flexWrap="wrap">
																{row.value.map((val, idx) => (
																	<Chip
																		key={idx}
																		label={val}
																		size="small"
																		style={{ marginRight: 8, marginBottom: 4 }}
																	/>
																))}
															</Box>
														) : (
															<Typography color="textSecondary">(Empty)</Typography>
														)
													) : (
														row.value
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>
				);
			})}
		</Box>
	);
};

export const renderAdjustPaneSize = (adjustPaneSizeData) => {
	if (!adjustPaneSizeData || typeof adjustPaneSizeData !== 'object') {
		return null;
	}

	const entries = [];
	if (adjustPaneSizeData.minSize !== undefined && adjustPaneSizeData.minSize !== null) {
		entries.push(['adjustPaneSize.minSize', adjustPaneSizeData.minSize]);
	}
	if (adjustPaneSizeData.size !== undefined && adjustPaneSizeData.size !== null) {
		entries.push(['adjustPaneSize.size', adjustPaneSizeData.size]);
	}

	if (entries.length === 0) {
		return null;
	}

	return (
		<TableContainer component={Paper} style={{ marginTop: 16 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Field</TableCell>
						<TableCell>Value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{entries.map(([field, value], index) => (
						<TableRow key={index}>
							<TableCell>{field}</TableCell>
							<TableCell>{String(value)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

